import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { isValidPairingCode, PAIRING_CODE_LENGTH, PAIRING_CODE_ALPHABET } from '@seencast/shared';
import { Modal } from '../../components/Modal.tsx';
import { Button, Field, TextField, SelectField } from '../../components/ui.tsx';
import { useScreens, usePairScreen } from '../../api/screens.ts';
import { ApiError } from '../../api/client.ts';
import { timezoneOptions, guessTimezone } from '../../lib/tz.ts';
import { ORIENTATIONS } from '@seencast/shared';

/** Keep only alphabet chars, uppercase, capped at the code length. */
export function sanitizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .filter((c) => PAIRING_CODE_ALPHABET.includes(c))
    .join('')
    .slice(0, PAIRING_CODE_LENGTH);
}

export function PairScreenModal({
  initialCode = '',
  onClose,
  onPaired,
}: {
  initialCode?: string;
  onClose: () => void;
  onPaired: (screenId: string) => void;
}) {
  const [code, setCode] = useState(sanitizeCode(initialCode));
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [screenId, setScreenId] = useState('');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [timezone, setTimezone] = useState(guessTimezone());
  const [submitted, setSubmitted] = useState(false);

  const screens = useScreens();
  const pair = usePairScreen();
  const tzOptions = useMemo(() => timezoneOptions(), []);
  const unpaired = (screens.data ?? []).filter((s) => s.status === 'never_paired');

  const codeValid = isValidPairingCode(code);
  const targetValid = mode === 'existing' ? !!screenId : name.trim().length > 0;
  const canSubmit = codeValid && targetValid && !pair.isPending;

  function submit() {
    setSubmitted(true);
    if (!codeValid || !targetValid) return;
    const body =
      mode === 'existing'
        ? { code, screen_id: screenId }
        : { code, new_screen: { name: name.trim(), location: location.trim() || undefined, orientation, timezone } };
    pair.mutate(body, {
      onSuccess: (screen) => {
        toast.success(`Paired “${screen.name}”`);
        onPaired(screen.id);
      },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Pairing failed'),
    });
  }

  return (
    <Modal
      title="Pair a new screen"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {pair.isPending ? 'Pairing…' : 'Pair screen'}
          </Button>
        </>
      }
    >
      <p className="text-dim mb-4">
        Enter the 6-character code shown on the screen, then choose which screen record it belongs
        to.
      </p>

      <Field
        label="Pairing code"
        hint={`${PAIRING_CODE_LENGTH} characters, letters and numbers (no O/0/I/1)`}
        error={submitted && !codeValid ? 'Enter the full 6-character code' : undefined}
        htmlFor="pair-code"
      >
        <input
          id="pair-code"
          value={code}
          onChange={(e) => setCode(sanitizeCode(e.target.value))}
          placeholder="e.g. 7KQP3M"
          className="mono"
          aria-invalid={submitted && !codeValid ? 'true' : undefined}
          style={{ letterSpacing: '4px', textTransform: 'uppercase' }}
          autoFocus
        />
      </Field>

      <div className="seg mb-4" role="tablist" aria-label="Screen record">
        <button
          type="button"
          className={mode === 'new' ? 'active' : ''}
          onClick={() => setMode('new')}
        >
          Create new
        </button>
        <button
          type="button"
          className={mode === 'existing' ? 'active' : ''}
          onClick={() => setMode('existing')}
          disabled={unpaired.length === 0}
          title={unpaired.length === 0 ? 'No unpaired screen records' : undefined}
        >
          Use existing ({unpaired.length})
        </button>
      </div>

      {mode === 'new' ? (
        <>
          <TextField
            label="Screen name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Front Window"
            error={submitted && !name.trim() ? 'A name is required' : undefined}
          />
          <TextField
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Glendale store, front window"
          />
          <div className="field-row">
            <SelectField
              label="Orientation"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as 'landscape' | 'portrait')}
            >
              {ORIENTATIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </SelectField>
            <SelectField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {tzOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </SelectField>
          </div>
        </>
      ) : (
        <SelectField
          label="Existing unpaired screen"
          value={screenId}
          onChange={(e) => setScreenId(e.target.value)}
          error={submitted && !screenId ? 'Pick a screen' : undefined}
        >
          <option value="">Select a screen…</option>
          {unpaired.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} {s.location ? `— ${s.location}` : ''}
            </option>
          ))}
        </SelectField>
      )}
    </Modal>
  );
}
